
import { switchMap, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Component, ViewChildren, QueryList, ViewChild, forwardRef, Input, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { SapiQueryResponse, SapiService } from '../../../services/sapi.service';
import { QueryParameter } from '../../../interfaces/queryString';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'bb-search-select',
	templateUrl: './bb-search-select.component.html',
	styleUrls: ['./bb-search-select.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbSearchSelectComponent),
		multi: true
	}]
})

export class BbSearchSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() allowClear = true;
	@Input() placeholder: string;
	@Input() entityType = 'mediaclip';
	@ViewChildren('selectedEntities') selectedEntities: QueryList<any>;
	@ViewChild('searchInput') searchInput: ElementRef;

	optionsPosition = 'below';
	paddingLeft: number;

	lastSelectedEntity: any;
	previousSelectedEntity: any;
	searchValue: string;
	selected: boolean;
	hide: boolean;
	searchEntities: boolean;

	results: any[];
	searchTermObservable: Subject<string>;
	searchKeyDown: Subject<KeyboardEvent>;
	searchId: Subject<string>;
	showEntities: boolean;

	searchTermSubscription: Subscription;
	searchKeyDownSubscription: Subscription;
	searchIdSubscription: Subscription;

	propagateChange: any;
	_onTouched: any;
	preloadedOptions: any[];
	optionsVisible: boolean;
	loading: boolean;
	preloadingOptions = false;

	constructor(private sapiService: SapiService, protected translate: TranslateService, protected hostElement: ElementRef) {
		this.searchTermObservable = new Subject<string>();
		this.searchKeyDown = new Subject<KeyboardEvent>();
		this.searchId = new Subject<string>();
		this.results = [];
		this.lastSelectedEntity = null;
		this.previousSelectedEntity = null;
		this.showEntities = false;
		this.paddingLeft = 8;
		this.selected = false;
		this.hide = false;
		this.searchEntities = true;
		this.placeholder = this.translate.instant('SELECT_ITEM');
		this.optionsVisible = false;
		this.loading = false;
	}

	ngOnInit() {
		if (this.lastSelectedEntity) {
			this.loading = true;
		}

		this.searchTermSubscription = this.searchTermObservable.pipe(
			debounceTime(400),
			distinctUntilChanged())
			.subscribe(() => {
				if (!this.searchValue && this.preloadedOptions) {
					this.results = this.preloadedOptions;
				} else {
					this.searchEntries(this.searchValue).subscribe((results: any[]) => this.results = results);
				}
			});
		this.searchKeyDownSubscription = this.searchKeyDown.subscribe((keyboardEvent: KeyboardEvent) => {
			if (keyboardEvent) {
				if (keyboardEvent.key === 'Escape') {
					this.searchEntities = false;
					this.showEntities = false;
					this.hide = false;
					this.toggleOptions(false);
					// } else if (keyboardEvent.key === 'Delete') {
					// 	this.removeLastSelectedEntity();
				} else {
					this.searchEntities = true;
					this.hide = true;
				}
			} else {
				this.searchEntities = true;
				this.hide = true;
			}
		});
		this.searchIdSubscription = this.searchId.subscribe((searchId: any) => {
			this.searchEntries(null, searchId).subscribe((results: any[]) => {
				if (results && results.length > 0) {
					this.updateSelectedEntity(results[0]);
				}
			});
		});
	}

	ngOnDestroy() {
		if (this.searchTermSubscription) {
			this.searchTermSubscription.unsubscribe();
		}
		if (this.searchKeyDownSubscription) {
			this.searchKeyDownSubscription.unsubscribe();
		}
		if (this.searchIdSubscription) {
			this.searchIdSubscription.unsubscribe();
		}
	}

	writeValue(value: string) {
		if (this.searchId && value) {
			this.searchId.next(value);
		} else {
			this.removeLastSelectedEntity(null, true);
		}
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: any): void {
		this._onTouched = fn;

	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.hostElement.nativeElement.style.pointerEvents = 'none';
			this.hostElement.nativeElement.classList.add('disabled');
		} else {
			this.hostElement.nativeElement.style.pointerEvents = 'all';
			this.hostElement.nativeElement.classList.remove('disabled');
		}

	}

	selectInput() {
		this.hide = false;
		this.selected = true;
	}

	leaveInput() {
		this.selected = false;
		if (this.lastSelectedEntity) {
			this.hide = false;
		}
		setTimeout(() => {
			this.showEntities = true;
		}, 300);
	}

	toggleOptions(visible?: boolean) {
		if (typeof visible === 'undefined') {
			visible = !this.optionsVisible;
		}

		this.optionsVisible = visible;

		if (visible && this.searchInput) {
			this.searchInput.nativeElement.focus();
		}
	}

	preloadOptions() {
		if (!this.preloadedOptions && !this.preloadingOptions) {
			this.preloadingOptions = true;
			this.searchEntries('').subscribe((results: any[]) => {
				this.preloadedOptions = this.results = results;
				this.preloadingOptions = false;
			});
		}
	}

	search(terms: Observable<string>) {
		return terms.pipe(debounceTime(500),
			distinctUntilChanged(),
			switchMap((term: string) => this.searchEntries(term)));
	}

	searchEntries(term: string, id?: string) {
		// console.warn('searchEntries term:', term, 'id', id);
		let query = '';
		let sortTerm = 'id';
		if (term) {
			query = `(title:${term} OR title:${term}* OR ${term})`;
			sortTerm = 'score';
		} else if (term === '') {
			query = '*:*';
		} else if (id) {
			query = 'id:' + id;
		}

		const queryParams: QueryParameter[] = [];
		queryParams.push({ name: 'q', values: [query] } as QueryParameter);

		this.results = [];

		return this.sapiService.query(this.entityType, queryParams, 0, 15, sortTerm, 'desc').pipe(map((data: SapiQueryResponse) => {
			const results: any[] = [];
			if (data.totalAmount > 0) {
				data.items.forEach((result: any) => {
					results.push({ label: result.title, value: result.id, data: result });
				});
			}
			return results;
		}));
	}

	selectLastEntity() {
		this.showEntities = true;
	}

	removeLastSelectedEntity(ev?: MouseEvent, ignoreChange?: boolean) {
		if (ev) {
			ev.stopPropagation();
		}
		// console.warn('removeLastSelectedEntity this.lastSelectedEntity', this.lastSelectedEntity);
		const lastValue = this.lastSelectedEntity && this.lastSelectedEntity.value;
		this.updateSelectedEntity(null);
		this.showEntities = true;
		if (this.propagateChange && lastValue) {
			this.propagateChange(null);
			this.previousSelectedEntity = this.lastSelectedEntity;
		}
	}

	selectEntity(event: Event, entity: any) {
		// console.warn('selectEntity', entity);
		this.updateSelectedEntity(entity);
		this.selectLastEntity();
		if (this.propagateChange && this.lastSelectedEntity && this.previousSelectedEntity !== this.lastSelectedEntity) {
			this.propagateChange(this.lastSelectedEntity.value);
			this.previousSelectedEntity = this.lastSelectedEntity;
		}
		this.toggleOptions(false);
		this.showEntities = true;
		this.searchInput.nativeElement.focus();
	}

	updateSelectedEntity(entity: any) {
		this.lastSelectedEntity = entity;
	}
}
